import { useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { utils, writeFile } from 'xlsx';

import { date as commonDate } from '@finley-technologies/common';

import {
  EventLogPaginatedFeedParamsGQLType,
  GetEventLogsQueryGQLType,
} from '../../../types/dashboard-core-gqlTypes.generated.d';
import { GET_EVENT_LOGS_QUERY } from './queries/GetEventLogs.gql';
import { EventLogExportDataRow } from './types';
import * as CreditFacilitiesEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import * as DeliverablesEntityConfig from '~/core/routes/deliverables/EntityConfig';
import * as FundingRequestsEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import * as InvoicesEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import * as UsersEntityConfig from '~/core/routes/users/EntityConfig';
import * as ValleyBankLoanAccountsEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { Icon } from '~/packages/common-frontend/components/icons';

// Types
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];

const organizeExportData = (logs: EventLogData[]) => {
  const headersToShow = new Set<string>();

  const data = logs.map((log) => {
    /* eslint-disable camelcase */
    const dataRow: EventLogExportDataRow = {
      eventId: log.eventId,
      eventLogType: log.eventLogType,
      entityId: log.entityId,
      eventMessage: log.eventMessage,
      createdAt: new Date(log.createdAt).toISOString(),
      updatedAt: new Date(log.updatedAt).toISOString(),
      actor_id: log.actor.id,
      actor_firstName: log.actor.firstName,
      actor_lastName: log.actor.lastName,
      actor_organization_id: log.actor.organization.id,
      actor_organization_name: log.actor.organization.name,
    };
    /* eslint-enable camelcase */

    // Run through each entity's exporter function. These functions add extra data onto
    // an existing row, and return the original row if the log doesn't match. This means
    // we can call them all in sequence to fill in all the necessary export data.
    const dataRowWithEntityData = [
      CreditFacilitiesEntityConfig.toExportDataRow,
      DeliverablesEntityConfig.toExportDataRow,
      FundingRequestsEntityConfig.toExportDataRow,
      LimitedPartnersEntityConfig.toExportDataRow,
      UsersEntityConfig.toExportDataRow,
      ValleyBankLoanAccountsEntityConfig.toExportDataRow,
      InvoicesEntityConfig.toExportDataRow,
    ].reduce(
      (prevDataRow, toExportDataRow) => toExportDataRow(prevDataRow, log),
      dataRow,
    );

    Object.keys(dataRowWithEntityData).forEach((key) => {
      headersToShow.add(key);
    });

    return dataRowWithEntityData;
  });

  return { data };
};

// Exports
type EventLogExportButtonProps = {
  filterParams: EventLogPaginatedFeedParamsGQLType;
};

export const EventLogExportButton = ({
  filterParams,
}: EventLogExportButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const client = useApolloClient();

  const fetchAllLogs = async (): Promise<EventLogData[]> => {
    const allItems: EventLogData[] = [];
    let before: string | undefined;
    let hasMore = true;

    // eslint-disable-next-line no-restricted-syntax
    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await client.query<GetEventLogsQueryGQLType>({
        query: GET_EVENT_LOGS_QUERY,
        // 'no-cache' is required here — 'network-only' still runs the cache's merge/read
        // type policies, which accumulate paginated results and cause every page fetch to
        // return all previously-seen items in addition to the new page, leading to duplicates.
        fetchPolicy: 'no-cache',
        variables: {
          params: {
            ...filterParams,
            // Set limit if desired
            // How many records to request per page when fetching all logs for export.
            // Must be <= backend MAX_PAGINATION_RESULTS (currently 20).
            before,
            after: undefined,
          },
        },
      });

      const page = data.eventLogs;
      allItems.push(...page.items);
      hasMore = page.hasMore;

      if (hasMore && page.items.length > 0) {
        // Use the oldest item in the page as the cursor for the next page.
        // Items are sorted desc (newest first), so the last item is the oldest.
        before = page.items[page.items.length - 1].eventId;
      }
    }

    return allItems;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setIsExporting(true);
    try {
      const allLogs = await fetchAllLogs();
      const { data } = organizeExportData(allLogs);

      const filenamePrefix = `event-log-export-${uuidV4().slice(-12)}`;
      const todayStr = commonDate.currentDateStr();
      const ws = utils.json_to_sheet(data);

      if (format === 'csv') {
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const downloadlink = document.createElement('a');
        downloadlink.href = url;
        downloadlink.download = `${filenamePrefix}-${todayStr}.csv`;
        downloadlink.click();
        URL.revokeObjectURL(url);
      } else {
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Data export');
        writeFile(wb, `${filenamePrefix}-${todayStr}.xlsx`);
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box style={{ alignSelf: 'self-end' }}>
      <Menu>
        <MenuButton
          as={Button}
          colorScheme="gray"
          size="sm"
          variant="secondary-muted"
          isLoading={isExporting}
          leftIcon={<Icon icon="arrow-down-to-bracket" />}
        >
          Export
        </MenuButton>
        <MenuList zIndex={100}>
          <MenuItem onClick={() => handleExport('csv')}>Download CSV</MenuItem>
          <MenuItem onClick={() => handleExport('xlsx')}>
            Download XLSX
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};
